import { ChevronIcon } from "../../../../materials/icons/chevron-icon/ChevronIcon";
import { IconButton } from "../../../icon-button/IconButton.Parts";
import {
  Button,
  Content,
  Placeholder,
  ToggleButtonContainer,
} from "../Trigger.Parts";
import {
  SelectionContainer,
  SelectionLabel,
} from "../single-select-content/SingleSelectContent.Parts";
import { Props } from "./Facade.Parts";

/**
 * A styled button that looks identical to the Select component's single-select
 * trigger but carries none of the select machinery. Use it as the visible
 * trigger for a BottomSheet (or any other overlay) where you want the
 * familiar select-trigger appearance with a simple onClick API.
 *
 * Pass `selection` (the current selected value array) and `getLabelForValue`
 * (a value-to-label resolver) to mirror the real Trigger's value display exactly.
 * Pass `placeholder` to show placeholder text when `selection` is empty.
 *
 * Accepts a ref that resolves to the underlying div element, so it can be used
 * as a DropdownList / floating-ui anchor.
 */
export const Facade = ({
  ref,
  isOpen,
  handleOpenChange,
  selection,
  getLabelForValue,
  placeholder,
  disabled,
  width,
  subtle,
  small,
}: Props) => {
  const hasSelection = selection.length > 0;

  return (
    <Button
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={disabled ? undefined : () => handleOpenChange(!isOpen)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (!disabled) handleOpenChange(!isOpen);
        }
      }}
      aria-expanded={isOpen}
      $active={isOpen}
      $disabled={disabled}
      $width={width}
      $subtle={subtle}
      $rows={1}
      $selection={hasSelection}
      $small={small}
    >
      <Content $rows={1} $multi={false} $active={hasSelection}>
        {placeholder && !hasSelection && (
          <Placeholder>{placeholder}</Placeholder>
        )}
        <SelectionContainer>
          {hasSelection && (
            <SelectionLabel>{getLabelForValue(selection[0])}</SelectionLabel>
          )}
        </SelectionContainer>
      </Content>
      <ToggleButtonContainer>
        <IconButton type="button" tabIndex={-1} $small disabled={disabled}>
          <ChevronIcon orientation={isOpen ? "up" : "down"} />
        </IconButton>
      </ToggleButtonContainer>
    </Button>
  );
};
